import i18n from "@i18n/i18n";
import ExpandIcon from "@mui/icons-material/Add";
import CollapseIcon from "@mui/icons-material/Remove";
import { Box, debounce, IconButton, Tooltip, useTheme } from "@mui/material";
import { GridCallbackDetails, GridCellParams, GridRowParams } from "@mui/x-data-grid";
import { GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD, GRID_TREE_DATA_GROUPING_FIELD } from "@mui/x-data-grid-premium";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import { MutableRefObject, useEffect, useRef } from "react";
import useState from "react-usestateref";
import useExpandAndCollapse from "../hooks/useExpandAndCollapse";

const groupingCellSelector = `[data-field='${GRID_TREE_DATA_GROUPING_FIELD}'], [data-field='${GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD}']`;

interface ExpanderProps {
  apiRef: MutableRefObject<GridApiPremium>;
}

const RowExpander = (props: ExpanderProps) => {
  const { apiRef } = props;
  const theme = useTheme();
  const [hoverTarget, setHoverTarget, hoverTargetRef] = useState<{ target: any; rowId: any } | null>(null);
  const expandRef = useRef<any>(null);
  const expandAndCollapse = useExpandAndCollapse(apiRef);

  useEffect(() => {
    return apiRef.current.subscribeEvent("rowMouseLeave", handleRowMouseLeave);
  }, [apiRef]);

  useEffect(() => {
    return apiRef.current.subscribeEvent("cellMouseOver", handleCellMouseOver);
  }, [apiRef]);

  useEffect(() => {
    //console.log("hoverTarget: ", hoverTarget);
    if (hoverTarget) {
      const viewportOffset = hoverTarget.target.getBoundingClientRect();
      const x = viewportOffset.right - expandRef.current.clientWidth - 4;
      const y = viewportOffset.top;

      expandRef.current.style.left = `${x}px`;
      expandRef.current.style.top = `${y}px`;
      expandRef.current.style.visibility = "visible";
    } else {
      expandRef.current.style.visibility = "hidden";
    }
  }, [hoverTarget]);

  const debouncedHandleMouseLeave = debounce(() => {
    setHoverTarget(null);
  }, 500);

  const handleCellMouseOver = (params: GridCellParams<any>, event: any, _details: GridCallbackDetails) => {
    //console.log("handleCellMouseOver: ", params, event, details);
    //console.log("hoverTarget: ", hoverTargetRef.current);

    const target = event.target?.closest(groupingCellSelector);
    //console.log("target: ", target);

    if (target) {
      debouncedHandleMouseLeave.clear();
      setHoverTarget({ target, rowId: params.id });
    } else {
      const target = event.target?.closest(".MuiDataGrid-row");
      //console.log("target: ", target);

      const rowId = target?.getAttribute("data-id");
      //console.log("rowId: ", rowId);

      if (hoverTargetRef.current?.rowId !== rowId) {
        //console.log("debouncedHandleMouseLeave: ", rowId);
        debouncedHandleMouseLeave();
      }
    }
  };

  const handleRowMouseLeave = (_params: GridRowParams<any>, event: any, _details: GridCallbackDetails) => {
    //console.log("handleRowMouseLeave: ", params, event, details);
    //console.log("hoverTarget: ", hoverTargetRef.current);

    const currentTarget = event.currentTarget?.closest(".MuiDataGrid-row");
    //console.log("currentTarget: ", currentTarget);

    const target = event.target?.closest(".MuiDataGrid-row");
    //console.log("target: ", target);

    const rowId = target?.getAttribute("data-id");
    //console.log("rowId: ", rowId);

    if (hoverTargetRef.current?.rowId !== rowId) {
      //console.log("debouncedHandleMouseLeave: ", rowId);
      debouncedHandleMouseLeave();
    }
  };

  const handleExpandRow = async (event: any) => {
    //console.log("handleExpandRow: ", hoverTarget);

    await expandAndCollapse.onExpandRow(event, hoverTarget?.rowId);
  };

  const handleCollapseRow = async (event: any) => {
    //console.log("handleCollapseRow: ", hoverTarget);

    await expandAndCollapse.onCollapseRow(event, hoverTarget?.rowId);
  };

  return (
    <Box
      ref={expandRef}
      className={"expander"}
      sx={{
        textAlign: "left",
        position: "fixed",
        marginTop: "1px",
        background: theme.palette.background.default,
        border: 1,
        borderRadius: 4,
        borderColor: "divider",
        padding: "1px",
        paddingTop: "2px",
      }}
    >
      <Tooltip title={i18n.t("row-expander.expand-all-button.tooltip")}>
        <IconButton aria-label="expand" size="small" onClick={handleExpandRow}>
          <ExpandIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title={i18n.t("row-expander.collapse-all-button.tooltip")}>
        <IconButton aria-label="collapse" size="small" onClick={handleCollapseRow}>
          <CollapseIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default RowExpander;
